import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiTrendingUp, FiHeart, FiX } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import blogService from '../../services/blogService';
import './BlogSearchBar.css';

const BlogSearchBar = ({ currentBlogId, showLikeCounts = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [topBlogs, setTopBlogs] = useState([]);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const history = useHistory();

  // Fetch top blogs with most likes on component mount
  useEffect(() => {
    const fetchTopBlogs = async () => {
      try {
        const response = await blogService.getBlogs();
        const blogsData = Array.isArray(response) ? response : response?.data || [];
        
        // Sort by likes and get top 5, exclude current blog
        const sortedBlogs = blogsData
          .filter(blog => blog.id !== currentBlogId)
          .sort((a, b) => (b.likes || 0) - (a.likes || 0))
          .slice(0, 5)
          .map(blog => ({
            id: blog.id,
            title: blog.title,
            likes: blog.likes || 0,
            category: blog.category,
            excerpt: blog.subject || blog.description || '',
            image: blog.coverImage || blog.image
          }));
        
        setTopBlogs(sortedBlogs);
      } catch (error) {
        console.error('Error fetching top blogs:', error);
      }
    };

    fetchTopBlogs();
  }, [currentBlogId]);

  // Debounced search function
  const searchBlogs = useCallback(async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await blogService.getBlogs();
      const blogsData = Array.isArray(response) ? response : response?.data || [];
      
      // Filter blogs based on search query
      const filtered = blogsData
        .filter(blog => 
          blog.id !== currentBlogId &&
          (blog.title?.toLowerCase().includes(query.toLowerCase()) ||
           blog.content?.toLowerCase().includes(query.toLowerCase()) ||
           blog.subject?.toLowerCase().includes(query.toLowerCase()) ||
           blog.category?.toLowerCase().includes(query.toLowerCase()))
        )
        .sort((a, b) => (b.likes || 0) - (a.likes || 0))
        .slice(0, 6)
        .map(blog => ({
          id: blog.id,
          title: blog.title,
          likes: blog.likes || 0,
          category: blog.category,
          excerpt: blog.subject || blog.description || '',
          image: blog.coverImage || blog.image,
        }));

      setSuggestions(filtered);
    } catch (error) {
      console.error('Error searching blogs:', error);
      toast.error('Eroare la căutare');
    } finally {
      setIsLoading(false);
    }
  }, [currentBlogId]);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        searchBlogs(searchTerm);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchBlogs]);

  // Highlight matching text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    return text.split(regex).map((part, index) =>
      part.toLocaleLowerCase('ro-RO') === query.toLocaleLowerCase('ro-RO')
        ? <mark key={`${part}-${index}`}>{part}</mark>
        : part
    );
  };

  // Handle suggestion click
  const handleSuggestionClick = (blogId) => {
    history.push(`/blog-single-fullwidth/${blogId}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      history.push(`/blog?search=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm('');
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="blog-search-container">
      <form onSubmit={handleSearchSubmit} className="blog-search-form">
        <div className="search-input-wrapper" ref={searchRef}>
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Caută articole despre uleiuri esențiale..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleInputFocus}
            className="search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="clear-search-btn"
              aria-label="Șterge căutarea"
            >
              <FiX />
            </button>
          )}
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div className="search-suggestions" ref={suggestionsRef}>
          {isLoading && (
            <div className="suggestion-loading">
              <div className="loading-spinner"></div>
              <span>Se caută...</span>
            </div>
          )}

          {/* Search Results */}
          {searchTerm && suggestions.length > 0 && (
            <div className="suggestions-section">
              <div className="suggestions-header">
                <FiSearch />
                <span>Rezultate pentru "{searchTerm}"</span>
              </div>
              {suggestions.map((blog) => (
                <div
                  key={blog.id}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(blog.id)}
                >
                  <div className="suggestion-image">
                    {blog.image ? (
                      <img src={blog.image} alt={blog.title} />
                    ) : (
                      <div className="placeholder-image">
                        <FiSearch />
                      </div>
                    )}
                  </div>
                  <div className="suggestion-content">
                    <h4 className="suggestion-title">{highlightText(blog.title, searchTerm)}</h4>
                    <p className="suggestion-excerpt">{blog.excerpt}</p>
                    <div className="suggestion-meta">
                      <span className="suggestion-category">{blog.category}</span>
                      {showLikeCounts && (
                        <span className="suggestion-likes">
                          <FiHeart />
                          {blog.likes}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {searchTerm && !isLoading && suggestions.length === 0 && (
            <div className="no-results">
              <FiSearch />
              <span>Nu s-au găsit rezultate pentru "{searchTerm}"</span>
              <button 
                onClick={() => history.push('/blog')}
                className="view-all-btn"
              >
                Vezi toate articolele
              </button>
            </div>
          )}

          {/* Top Articles (when no search) */}
          {!searchTerm && topBlogs.length > 0 && (
            <div className="suggestions-section">
              <div className="suggestions-header">
                <FiTrendingUp />
                <span>Articole populare</span>
              </div>
              {topBlogs.map((blog) => (
                <div
                  key={blog.id}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(blog.id)}
                >
                  <div className="suggestion-image">
                    {blog.image ? (
                      <img src={blog.image} alt={blog.title} />
                    ) : (
                      <div className="placeholder-image">
                        <FiTrendingUp />
                      </div>
                    )}
                  </div>
                  <div className="suggestion-content">
                    <h4 className="suggestion-title">{blog.title}</h4>
                    <p className="suggestion-excerpt">{blog.excerpt}</p>
                    <div className="suggestion-meta">
                      <span className="suggestion-category">{blog.category}</span>
                      {showLikeCounts && (
                        <span className="suggestion-likes">
                          <FiHeart />
                          {blog.likes}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogSearchBar;
